<template>
  <div class="account-info-ctn">
    <h2 class="header-text">{{ $t('profile.my_subscription') }}</h2>
    <v-container class="mt-6 ma-0">
      <v-row>
        <v-col cols="6">
          <div class="label">{{ $t('current_plan') }}</div>
            <div class="font-weight-light d-flex align-center">
            {{ getPlanName(currentPlan) }}
            <span v-if="!isFreePlan" class="extra-tag ml-2" :class="{'trial': currentPlan?.id === 'elabel-trial', 'canceled': contract?.external_subscription_data?.scheduled_change?.action === 'cancel' || contract?.external_subscription_data?.scheduled_change?.action === 'pause' || contract?.cancellation_date}">
              <span v-if="contract?.external_subscription_data?.scheduled_change?.action === 'cancel' || contract?.external_subscription_data?.scheduled_change?.action === 'pause' || contract?.cancellation_date">{{ $t('canceled') }}</span>
              <span v-else-if="currentPlan?.id === 'elabel-trial'">{{ $t('trial') }}</span>
            </span>
          </div>
        </v-col>

        <v-col v-if="contract?.external_subscription_data?.scheduled_change?.action">
          <div class="label" v-if="contract?.external_subscription_data?.scheduled_change?.action === 'cancel' || contract?.external_subscription_data?.scheduled_change?.action === 'pause'">{{ $t('ends_on') }}</div>
          <div class="label" v-if="contract?.external_subscription_data?.scheduled_change?.action === 'resume'">{{ $t('resumes_on') }}</div>
          <div class="font-weight-light">
            {{ formatDate(contract?.external_subscription_data?.scheduled_change?.effective_at) }}
          </div>
        </v-col>

        <v-col v-if="!isFreePlan" cols="6">
          <div class="label">{{ $t('total_qr_codes') }}</div>
          <div class="font-weight-light capitalize">{{ currentPlan?.definition?.['quota:code'] ? formatNumber(currentPlan.definition['quota:code']) : $t('unlimited') }}</div>
        </v-col>

        <!-- <v-col v-if="!isFreePlan" cols="6">
          <div class="label">{{ $t('total_seats') }}</div>
          <div class="font-weight-light">{{ currentPlan.definition?.['quota:user'] }}</div>
        </v-col> -->

        <v-col cols="6" v-if="contract?.valid_to">
          <div class="label" v-if="!isTrialPlan">{{ $t('next_payment') }}</div>
          <div class="label" v-else>{{ $t('trial_ends_on') }}</div>
          <div class="font-weight-light">{{ formatDate(contract?.valid_to) }}</div>
        </v-col>

        <v-col cols="6" v-if="(contract?.valid_to && !hasPendingChanges && !hasPausedContract && !isTrialPlan)">
          <v-btn :disable="loading" @click="cancelSubscriptionDialog = true" outlined color="red">{{ $t('cancel_subscription') }}</v-btn>
        </v-col>

        <v-col cols="6" v-if="hasPendingChanges">
          <v-btn color="primary" :loading="loading" @click="resumeSubscriptionDialog = true" outlined>{{ $t('resume_subscription') }}</v-btn>
        </v-col>

        <v-col cols="6" v-else-if="hasPausedContract">
          <v-btn color="primary" :loading="loading" @click="resumeSubscriptionDialog = true" outlined>{{ $t('resume_subscription') }}</v-btn>
        </v-col>

        <v-col cols="12" v-else-if="isFreePlan">
          <v-btn @click="goToUpgrade()" outlined color="primary">{{ $t('subscribe') }}</v-btn>
        </v-col>

      </v-row>
    </v-container>

    <h2 v-if="!isFreePlan && !isTrialPlan" class="header-text mt-6">{{ $t('profile.billing_information') }}</h2>
    <v-container v-if="!isFreePlan && !isTrialPlan" class="mt-6 ma-0">
      <billing-customer ref="billing-customer-form" :customer="customer"></billing-customer>
      <billing-business ref="billing-business-form" :country="subscriptionAddress?.country_code || ''" :business="subscriptionBusiness || {}"></billing-business>

      <billing-address ref="billing-address-form" :address="subscriptionAddress || {}"></billing-address>

      <v-row>
        <!-- <v-col cols="12" md="6">
          <div class="label">{{ $t('profile.company_name') }}</div>
          <div class="font-weight-light">{{ profile.company?.name }}</div>
        </v-col> -->
        <v-col cols="6">
          <v-btn class="mb-2" :disabled="loading" raised color="primary" @click="updateBillingInformation()">{{ $t('profile.update') }}</v-btn>
        </v-col>

        <v-col cols="6">
          <div class="paddle-portal-btn font-weight-light" @click="goToPaddleBillingPortal()">{{ $t('profile.paddle_billing_details') }}</div>
        </v-col>
        <!--
        <v-col cols="12" md="6">
          <div class="label">{{ $t('profile.email') }}</div>
          <div class="font-weight-light">{{ profile.email }}</div>
        </v-col> -->
      </v-row>
    </v-container>

    <v-dialog v-model="resumeSubscriptionDialog" width="550">
      <v-card>
        <v-card-title>{{$t('resume_subscription')}}</v-card-title>
        <v-card-text class="py-5 px-5 black--text">
          <div v-html="$t('resume_subscription_modal_description', { planName: getPlanName(hasPendingChanges ? currentPlan : hasPausedContract?.plan) })"></div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="resumeSubscriptionDialog = false">{{$t('close')}}</v-btn>
          <v-btn primary :loading="loading" @click="hasPendingChanges ? undoPendingChanges() : resumeSubscription()" color="primary">{{ $t('confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelSubscriptionDialog" width="550">
      <v-card>
        <v-card-title>{{$t('cancel_subscription')}}</v-card-title>
        <v-card-text class="py-5 px-5 black--text">
          <div v-html="$t('cancel_subscription_modal_description', { planName: getPlanName(currentPlan), billingDate: formatDate(contract?.valid_to) })"></div>
          <div>
            <v-checkbox
              v-model="keepPaymentInfo"
              :label="$t('keep_payment_information')"
              :hint="$t('keep_payment_information_hint')"
              persistent-hint
              class="black--text"
            ></v-checkbox>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelSubscriptionDialog = false">{{$t('close')}}</v-btn>
          <v-btn :disabled="cancelling" @click="cancelSubscription()" color="error">{{ $t('cancel_subscription') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'
import amplitude from 'amplitude-js'
import Utils from '@/utils/utils'
import BillingAddress from '@/components/forms/BillingAddress'
import BillingCustomer from '@/components/forms/BillingCustomer'
import BillingBusiness from '@/components/forms/BillingBusiness'
import { getPlanName } from '@/const/plans'

export default {
  components: { BillingAddress, BillingCustomer, BillingBusiness },
  name: 'SubscriptionAndBilling',
  computed: {
    ...mapGetters(['currentPlan', 'contract', 'profile', 'hasPausedContract', 'customer', 'subscriptionAddress', 'subscriptionBusiness', 'subscription']),

    isTrialPlan () {
      return this.currentPlan?.id === 'st-pro-trial'
    },

    isFreePlan () {
      return this.currentPlan?.id.indexOf('st-pro-free') > -1
    },

    hasPendingChanges () {
      return this.contract?.external_subscription_data?.scheduled_change?.action === 'cancel' || this.contract?.external_subscription_data?.scheduled_change?.action === 'pause'
    }
  },
  data () {
    return {
      cancelling: false,
      cancelSubscriptionDialog: false,
      resumeSubscriptionDialog: false,
      keepPaymentInfo: true,
      loading: false
    }
  },
  methods: {
    getPlanName,

    formatNumber (value) {
      return Utils.formatNumber(Number(value), this.$i18n.locale)
    },

    async updateBillingInformation () {
      this.loading = true
      try {
        await this.$nextTick()

        const validations = [
          this.$refs['billing-address-form'].validate(),
          this.$refs['billing-business-form'].validate(),
          this.$refs['billing-customer-form'].validate()
        ]

        if (validations.some(v => !v)) {
          this.$store.dispatch('showSnackBar', { message: this.$t('profile.error_updating_billing') })
          return
        }

        const results = await Promise.allSettled([
          this.$refs['billing-address-form'].submit(),
          this.$refs['billing-business-form'].submit(),
          this.$refs['billing-customer-form'].submit()
        ])

        if (results.some(r => r.status === 'rejected')) {
          this.$store.dispatch('showSnackBar', { message: this.$t('profile.error_updating_billing') })
          return
        }

        amplitude.getInstance().logEvent('Billing Info Updated')
        this.$store.dispatch('showSnackBar', { message: this.$t('profile.billing_update_success') })
      } finally {
        this.loading = false
      }
    },

    async goToUpgrade () {
      this.$router.push({ name: 'upgrade', query: { source: 'subscription_settings' } })
    },

    async goToPaddleBillingPortal () {
      if (this.subscription?.management_urls?.general) {
        window.open(this.subscription?.management_urls?.general, '_blank')
      }
    },

    async pauseSubscription () {
      this.cancelling = true
      await this.$store.dispatch('pausePlan')
      setTimeout(() => {
        this.cancelSubscriptionDialog = false
        this.$store.dispatch('loadUserProfile')
        this.$store.dispatch('loadUserContract', { id: this.profile.plan?.contract_id })
        this.cancelling = false
      }, 2000)
    },

    async undoPendingChanges () {
      this.loading = true
      await this.$store.dispatch('undoPendingChange')
      setTimeout(() => {
        this.$store.dispatch('loadUserProfile')
        this.$store.dispatch('loadUserContract', { id: this.profile.plan?.contract_id })
        this.loading = false
        this.resumeSubscriptionDialog = false
      }, 2000)
    },

    async resumeSubscription () {
      this.loading = true
      await this.$store.dispatch('resumePlan')
      setTimeout(() => {
        this.$store.dispatch('loadUserProfile')
        this.$store.dispatch('loadUserContracts')
        this.$store.dispatch('loadUserContract', { id: this.hasPausedContract?.id })
        this.loading = false
        this.resumeSubscriptionDialog = false
      }, 2000)
    },

    async cancelSubscription () {
      if (this.keepPaymentInfo) {
        this.pauseSubscription()
        return
      }
      this.cancelling = true
      await this.$store.dispatch('cancelPlan')
      setTimeout(() => {
        this.cancelSubscriptionDialog = false
        this.$store.dispatch('loadUserProfile')
        this.$store.dispatch('loadUserContract', { id: this.profile.plan?.contract_id })
        this.cancelling = false
      }, 2000)
    },

    formatDate (date) {
      return Utils.formatDate(date, true)
    }
  }
}
</script>

<style lang="sass" scoped>

.account-info-ctn
  min-height: calc(100vh + 50px)
  padding-bottom: 50px

.header-text
  color: #242424
  font-size: 26px
  font-style: normal
  font-weight: 500
  line-height: 24px

.extra-tag
  color: white
  font-size: 12px
  padding: 2px 6px
  &.trial
    background-color: #0281c9
  &.canceled
    background-color: #F22A42

:deep(.v-input--checkbox .v-label)
  color: black !important
  font-weight: 600

.custom-v-select::v-deep input
  display: none !important

.paddle-portal-btn
  cursor: pointer
  text-align: right
  text-decoration: underline
  color: #0070B6
  font-feature-settings: 'liga' off, 'clig' off
  line-height: 24px
  letter-spacing: 0.15px
  text-decoration-line: underline
  text-decoration-style: solid
  text-decoration-skip-ink: none
  text-decoration-thickness: auto
  text-underline-offset: auto
  text-underline-position: from-font
</style>
